import { useState, useEffect, useCallback } from "react";
import { getViewConfigByName } from "../services/viewConfigService";

/**
 * Reusable custom hook to fetch a ViewConfig from the backend.
 * Automatically re-fetches when a `viewconfig:invalidate` CustomEvent is
 * dispatched on `window` with `event.detail.name` matching this hook's name.
 *
 * @param {string} name - The configuration name (e.g. "HOME_SECTIONS")
 * @returns {{ data: Object|null, loading: boolean, error: Error|null, refetch: Function }}
 */
export const useViewConfig = (name) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    if (!name) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await getViewConfigByName(name);
      setData(result);
    } catch (err) {
      setError(err);
      console.error(`useViewConfig error for "${name}":`, err);
    } finally {
      setLoading(false);
    }
  }, [name]);

  // Fetch on mount / name change
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Listen for admin invalidation broadcasts
  useEffect(() => {
    if (!name) return;
    const handler = (e) => {
      if (e.detail?.name === name) {
        fetchConfig();
      }
    };
    window.addEventListener("viewconfig:invalidate", handler);
    return () => window.removeEventListener("viewconfig:invalidate", handler);
  }, [name, fetchConfig]);

  return { data, loading, error, refetch: fetchConfig };
};

